import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";
import { Link } from "react-router-dom";
import apiService, { getImageUrl } from "../../services/api";

// Updated at 01:20 - Added beautiful navigation arrows!

// Fallback destinations if API fails
const fallbackDestinations = [
  { id: 1, name: "Maldives", listings: 15, image: "/assets/img/destination/destination_1_1.jpg" },
  { id: 2, name: "Thailand", listings: 22, image: "/assets/img/destination/destination_1_2.jpg" },
  { id: 3, name: "Belgium", listings: 25, image: "/assets/img/destination/destination_1_3.jpg" },
  { id: 4, name: "Island", listings: 28, image: "/assets/img/destination/destination_1_4.jpg" },
  { id: 5, name: "Maldives", listings: 30, image: "/assets/img/destination/destination_1_5.jpg" },
];

const sliderOptions = {
    modules: [EffectCoverflow, Navigation],
    effect: "coverflow",
    centeredSlides: true,
    slidesPerView: "5",
    initialSlide: 1,
    grabCursor: true,
    loop: true, // Enable infinite loop
    speed: 1500,
    navigation: {
      nextEl: ".destination-slider-next",
      prevEl: ".destination-slider-prev",
    },
    coverflowEffect: {
      rotate: 0,
      stretch: 95,
      depth: 212,
      modifier: 1,
    },
    breakpoints: {
      0: { slidesPerView: 1 },
      576: { slidesPerView: 2 },
      992: { slidesPerView: 3 },
      1200: { slidesPerView: 3 },
    },
  };
  

// Custom navigation arrow styles
const navButtonStyle = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '50px',
  height: '50px',
  backgroundColor: '#ffffff',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: '#e1e8ed',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontSize: '18px',
  color: '#5a7184',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  zIndex: 10,
  opacity: 0.8,
};

// Mobile responsive style - hide arrows on small screens
const navButtonResponsiveStyle = (window.innerWidth < 992) ? { display: 'none' } : {};

const navButtonHoverStyle = {
  backgroundColor: '#5A9FD7',
  borderColor: '#5A9FD7',
  color: '#ffffff',
  transform: 'translateY(-50%) scale(1.1)',
  opacity: 1,
  boxShadow: '0 6px 20px rgba(90, 159, 215, 0.3)',
};

function DestinationOne() {
  const [cursorActive, setCursorActive] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prevHover, setPrevHover] = useState(false);
  const [nextHover, setNextHover] = useState(false);

  // Fetch featured destinations from API
  useEffect(() => {
    const fetchFeaturedDestinations = async () => {
      try {
        setLoading(true);
        console.log('DestinationOne: Fetching featured destinations...');
        
        // Fetch featured destinations using dedicated endpoint
        const response = await apiService.getFeaturedDestinations();
        console.log('DestinationOne: API response:', response);
        
        // Map API data to component format
        const mappedDestinations = response.results?.map(dest => {
          const mappedDest = {
            id: dest.id,
            name: dest.name,
            listings: dest.review_count || 0, // Use review count or 0
            image: getImageUrl(dest.main_image) || '/assets/img/destination/destination_1_1.jpg',
            slug: dest.slug,
            city: dest.city,
            country: dest.country,
            price: dest.price
          };
          console.log('DestinationOne: Mapped destination:', mappedDest);
          return mappedDest;
        }) || [];
        
        console.log('DestinationOne: All featured destinations loaded:', mappedDestinations);
        setDestinations(mappedDestinations.length > 0 ? mappedDestinations : fallbackDestinations);
        setError(null);
        
      } catch (error) {
        console.error('DestinationOne: Error fetching featured destinations:', error);
        setError(error.message);
        
        // Use fallback data if API fails
        setDestinations(fallbackDestinations);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedDestinations();
  }, []);

  // Add event listeners for hover effect
  useEffect(() => {
    const sliderWrap = document.querySelector(".slider-drag-wrap");
    const sliderLink = document.querySelectorAll(".slider-drag-wrap a");

    const handleMouseEnter = () => setCursorActive(true);
    const handleMouseLeave = () => setCursorActive(false);
    
    if (sliderWrap) {
      sliderWrap.addEventListener("mouseenter", handleMouseEnter);
      sliderWrap.addEventListener("mouseleave", handleMouseLeave);
    }
    
    sliderLink.forEach(link => {
      link.addEventListener("mouseenter", () => setCursorActive(false));
      link.addEventListener("mouseleave", () => setCursorActive(true));
    });

    // Clean up event listeners on component unmount
    return () => {
      if (sliderWrap) {
        sliderWrap.removeEventListener("mouseenter", handleMouseEnter);
        sliderWrap.removeEventListener("mouseleave", handleMouseLeave);
      }
      
      sliderLink.forEach(link => {
        link.removeEventListener("mouseenter", () => setCursorActive(false));
        link.removeEventListener("mouseleave", () => setCursorActive(true));
      });
    };
  }, []);

  return (
    <div className="position-relative overflow-hidden">
      <div className="container">
        <div className="title-area text-center">
          <span className="sub-title">Top Destination</span>
          <h2 className="sec-title">Popular Destination</h2>
        </div>

        <div className={`slider-drag-wrap position-relative ${cursorActive ? 'active' : ''}`}>
          {/* Custom Navigation Arrows */}
          <div 
            className="destination-slider-prev"
            style={{
              ...navButtonStyle,
              left: '-25px',
              ...(prevHover ? navButtonHoverStyle : {})
            }}
            onMouseEnter={() => setPrevHover(true)}
            onMouseLeave={() => setPrevHover(false)}
          >
            <i className="fas fa-chevron-left"></i>
          </div>
          <div 
            className="destination-slider-next"
            style={{
              ...navButtonStyle,
              right: '-25px',
              ...(nextHover ? navButtonHoverStyle : {})
            }}
            onMouseEnter={() => setNextHover(true)}
            onMouseLeave={() => setNextHover(false)}
          >
            <i className="fas fa-chevron-right"></i>
          </div>
          
          <Swiper {...sliderOptions} className="destination-slider">
            {destinations.map((dest) => (
              <SwiperSlide key={dest.id}>
                <div className="destination-box gsap-cursor">
                  <div className="destination-img">
                    <img src={dest.image} alt={dest.name} />
                    <div className="destination-content">
                      <div className="media-left">
                        <h4 className="box-title">
                          <Link to={`/destination/${dest.slug || dest.id}`}>{dest.name}</Link>
                        </h4>
                        <span className="destination-subtitle">{dest.listings} Listing</span>
                      </div>
                      <div>
                        <Link to="/destination" className="th-btn style2 th-icon">
                          View All
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
}

export default DestinationOne;
